import React from "react";
import { NavLink, Outlet } from "react-router-dom";

/**
 * TabbedHub — shared super-page layout used by every v3.1 hub.
 *
 * Renders a compact horizontal tab strip and delegates the actual
 * page body to the nested route via <Outlet />. Each sub-page keeps
 * its own component + its own H1 — the hub itself no longer prints a
 * duplicate title, which reclaimed ~250 vertical pixels above the
 * fold and matched the tab-first pattern used by Samsara / HubSpot.
 *
 * The `title` / `subtitle` props are still accepted for backwards
 * compatibility but ignored — sub-pages own the header now.
 *
 * Props
 * -----
 *   testid   Wrapper data-testid; each tab exposes `${testid}-tab-${key}`.
 *   tabs     Array of { key, label, to, icon? } — order defines UI order.
 */
export default function TabbedHub({ tabs = [], testid = "tabbed-hub" }) {
  return (
    <div className="min-h-screen text-white" style={{ background: "#0A0A0A" }} data-testid={testid}>
      <div
        className="px-6 lg:px-8 flex items-center gap-1 overflow-x-auto"
        style={{
          borderBottom: "1px solid #262626",
          height: 42,
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "rgba(10,10,10,0.94)",
          backdropFilter: "blur(8px)",
        }}
        data-testid={`${testid}-tabs`}
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.key}
              to={t.to}
              end={!!t.exact}
              className={({ isActive }) => `
                inline-flex items-center gap-1.5 px-3 h-full text-[12px] font-medium
                whitespace-nowrap transition-colors relative
                ${isActive ? "text-white" : "text-neutral-400 hover:text-neutral-200"}
              `}
              style={({ isActive }) => ({
                borderBottom: isActive ? "2px solid #E50914" : "2px solid transparent",
                marginBottom: "-1px",
              })}
              data-testid={`${testid}-tab-${t.key}`}
            >
              {Icon && <Icon size={12} weight="bold" />}
              {t.label}
            </NavLink>
          );
        })}
      </div>
      <div data-testid={`${testid}-content`}>
        <Outlet />
      </div>
    </div>
  );
}
